import type { FC, ReactNode } from 'react';
import ArrowLeftIcon from '@untitled-ui/icons-react/build/esm/ArrowLeft';
import { Box, Container, Stack, SvgIcon, Typography } from '@mui/material';
import { RouterLink } from 'src/components/router-link';
import { Seo } from 'src/components/seo';
import { usePageView } from 'src/hooks/use-page-view';
import { paths } from 'src/paths';
import type { Page as PageType } from 'src/types/page';

interface Section {
  num: string;
  title: string;
  body: ReactNode;
}

const bodyText = {
  fontFamily: '"Inter", sans-serif',
  fontSize: { xs: 16, md: 17 },
  lineHeight: 1.7,
  color: '#27272a'
} as const;

const subhead = {
  fontFamily: '"Inter", sans-serif',
  fontSize: { xs: 15, md: 16 },
  fontWeight: 600,
  color: '#0a0a0b',
  mb: 1,
  mt: 2.5
} as const;

const ulStyle = {
  pl: 2.5,
  m: 0,
  '& li': { mb: 1.25 }
} as const;

const sections: Section[] = [
  {
    num: '01',
    title: 'What we collect',
    body: (
      <>
        <Typography sx={subhead}>Information you give us</Typography>
        <Typography sx={bodyText}>
          When you create an account, request a quote, schedule a consultation, contact support, subscribe to communications, or connect a third-party service, you may share:
        </Typography>
        <Box component="ul" sx={{ ...ulStyle, mt: 1.5 }}>
          <Typography component="li" sx={bodyText}>Name, email, phone, and mailing address</Typography>
          <Typography component="li" sx={bodyText}>Billing and payment information</Typography>
          <Typography component="li" sx={bodyText}>Project details (branding briefs, website content, marketing goals, AI automation scope)</Typography>
          <Typography component="li" sx={bodyText}>Communication preferences</Typography>
        </Box>

        <Typography sx={subhead}>Information collected automatically</Typography>
        <Box component="ul" sx={ulStyle}>
          <Typography component="li" sx={bodyText}>Device information (browser, operating system, device identifiers)</Typography>
          <Typography component="li" sx={bodyText}>Log data (IP address, access times, pages viewed)</Typography>
          <Typography component="li" sx={bodyText}>Location information, when you allow it</Typography>
          <Typography component="li" sx={bodyText}>Cookies and similar technologies (see Section 08)</Typography>
        </Box>

        <Typography sx={subhead}>Information from connected services</Typography>
        <Typography sx={bodyText}>
          When you authorize a third-party connection (such as QuickBooks for invoicing or Google for calendar integration), we receive only the data you authorize and use it only to deliver the integrated feature you requested.
        </Typography>
      </>
    )
  },
  {
    num: '02',
    title: 'How we use it',
    body: (
      <Box component="ul" sx={ulStyle}>
        <Typography component="li" sx={bodyText}>Provide, maintain, and improve our services</Typography>
        <Typography component="li" sx={bodyText}>Process transactions, quotes, and invoices</Typography>
        <Typography component="li" sx={bodyText}>Plan, schedule, and deliver creative and marketing projects</Typography>
        <Typography component="li" sx={bodyText}>Communicate with you about your account, projects, and service updates</Typography>
        <Typography component="li" sx={bodyText}>Send promotional communications, only with your consent</Typography>
        <Typography component="li" sx={bodyText}>Respond to support requests</Typography>
        <Typography component="li" sx={bodyText}>Analyze usage to improve experience and quality</Typography>
        <Typography component="li" sx={bodyText}>Detect, prevent, and address technical issues or fraud</Typography>
        <Typography component="li" sx={bodyText}>Meet legal obligations</Typography>
      </Box>
    )
  },
  {
    num: '03',
    title: 'How we share it',
    body: (
      <>
        <Typography sx={{ ...bodyText, mb: 2, fontWeight: 500, color: '#0a0a0b' }}>
          We do not sell your personal information.
        </Typography>
        <Typography sx={bodyText}>
          We share information only in these circumstances:
        </Typography>
        <Box component="ul" sx={{ ...ulStyle, mt: 1.5 }}>
          <Typography component="li" sx={bodyText}>
            <strong style={{ color: '#0a0a0b' }}>Service providers</strong> who help us operate (payment processors, email tools, cloud hosting, analytics).
          </Typography>
          <Typography component="li" sx={bodyText}>
            <strong style={{ color: '#0a0a0b' }}>Creative collaborators</strong> and contractors who help us deliver your project.
          </Typography>
          <Typography component="li" sx={bodyText}>
            <strong style={{ color: '#0a0a0b' }}>Legal requirements</strong> when required by law, court order, or government request.
          </Typography>
          <Typography component="li" sx={bodyText}>
            <strong style={{ color: '#0a0a0b' }}>Protection of rights</strong> to defend our rights, your safety, or the public.
          </Typography>
          <Typography component="li" sx={bodyText}>
            <strong style={{ color: '#0a0a0b' }}>Business transfers</strong> in connection with a merger, acquisition, or sale of assets.
          </Typography>
          <Typography component="li" sx={bodyText}>
            <strong style={{ color: '#0a0a0b' }}>With your consent</strong> when you explicitly authorize the sharing.
          </Typography>
        </Box>
      </>
    )
  },
  {
    num: '04',
    title: 'Third-party integrations',
    body: (
      <>
        <Typography sx={bodyText}>
          Our platform may integrate with:
        </Typography>
        <Box component="ul" sx={{ ...ulStyle, mt: 1.5 }}>
          <Typography component="li" sx={bodyText}>
            <strong style={{ color: '#0a0a0b' }}>QuickBooks</strong> for invoicing, payment processing, and financial management. We sync only the invoices, customers, and payments needed for the integration.
          </Typography>
          <Typography component="li" sx={bodyText}>
            <strong style={{ color: '#0a0a0b' }}>Auth0</strong> for secure authentication and identity management.
          </Typography>
          <Typography component="li" sx={bodyText}>
            <strong style={{ color: '#0a0a0b' }}>Supabase</strong> for database services and storage of customer records, quotes, and invoices.
          </Typography>
          <Typography component="li" sx={bodyText}>
            <strong style={{ color: '#0a0a0b' }}>Google services</strong> for authentication, calendar, and email.
          </Typography>
          <Typography component="li" sx={bodyText}>
            <strong style={{ color: '#0a0a0b' }}>Payment processors</strong> for secure transaction handling.
          </Typography>
        </Box>
        <Typography sx={{ ...bodyText, mt: 2 }}>
          Each service has its own privacy policy. Review theirs before authorizing a connection. You can disconnect any third-party integration from your account settings at any time.
        </Typography>
      </>
    )
  },
  {
    num: '05',
    title: 'Security',
    body: (
      <>
        <Typography sx={bodyText}>
          We use appropriate technical and organizational safeguards, including:
        </Typography>
        <Box component="ul" sx={{ ...ulStyle, mt: 1.5 }}>
          <Typography component="li" sx={bodyText}>Encryption of data in transit and at rest</Typography>
          <Typography component="li" sx={bodyText}>Secure authentication mechanisms</Typography>
          <Typography component="li" sx={bodyText}>Regular security review and updates</Typography>
          <Typography component="li" sx={bodyText}>Access controls limiting who can view your data</Typography>
          <Typography component="li" sx={bodyText}>Server-side storage for API keys and credentials</Typography>
        </Box>
        <Typography sx={{ ...bodyText, mt: 2 }}>
          No method of transmission or storage is 100% secure. We cannot guarantee absolute security.
        </Typography>
      </>
    )
  },
  {
    num: '06',
    title: 'How long we keep it',
    body: (
      <>
        <Typography sx={bodyText}>
          We retain your information for as long as needed to provide services, meet tax and contract record-keeping obligations, and resolve disputes. Once it is no longer needed, we delete or anonymize it.
        </Typography>
      </>
    )
  },
  {
    num: '07',
    title: 'Your rights',
    body: (
      <>
        <Typography sx={bodyText}>
          Depending on where you live, you may have the right to:
        </Typography>
        <Box component="ul" sx={{ ...ulStyle, mt: 1.5 }}>
          <Typography component="li" sx={bodyText}>
            <strong style={{ color: '#0a0a0b' }}>Access</strong> a copy of the personal information we hold about you.
          </Typography>
          <Typography component="li" sx={bodyText}>
            <strong style={{ color: '#0a0a0b' }}>Correct</strong> inaccurate or incomplete information.
          </Typography>
          <Typography component="li" sx={bodyText}>
            <strong style={{ color: '#0a0a0b' }}>Delete</strong> your information, subject to legal retention requirements.
          </Typography>
          <Typography component="li" sx={bodyText}>
            <strong style={{ color: '#0a0a0b' }}>Port</strong> your data in a portable format.
          </Typography>
          <Typography component="li" sx={bodyText}>
            <strong style={{ color: '#0a0a0b' }}>Opt out</strong> of marketing communications at any time.
          </Typography>
          <Typography component="li" sx={bodyText}>
            <strong style={{ color: '#0a0a0b' }}>Disconnect</strong> any authorized third-party integration.
          </Typography>
        </Box>
        <Typography sx={{ ...bodyText, mt: 2 }}>
          Email us to exercise any of these rights. Details are at the bottom of the page.
        </Typography>
      </>
    )
  },
  {
    num: '08',
    title: 'Cookies and tracking',
    body: (
      <>
        <Typography sx={bodyText}>
          We use cookies and similar technologies to keep you signed in, remember preferences, analyze how the services are used, and deliver relevant content.
        </Typography>
        <Typography sx={{ ...bodyText, mt: 2 }}>
          You can control cookies in your browser settings. Disabling them may affect functionality.
        </Typography>
      </>
    )
  },
  {
    num: '09',
    title: "Children's privacy",
    body: (
      <Typography sx={bodyText}>
        Our services are not intended for users under 18. We do not knowingly collect information from children. If we learn we have, we will delete it promptly.
      </Typography>
    )
  },
  {
    num: '10',
    title: 'Changes to this policy',
    body: (
      <Typography sx={bodyText}>
        We may update this policy. Material changes will be reflected by an updated "Last Updated" date and, where appropriate, by direct notice. Review periodically.
      </Typography>
    )
  }
];

const Page: PageType = () => {
  usePageView();

  return (
    <>
      <Seo
        title="Privacy Policy | Mystic Media Film"
        description="Mystic Media Film privacy policy. How we collect, use, store, and protect personal data when you visit our site or engage our services."
      />
      <Box component="main" sx={{ background: '#ffffff', minHeight: '100vh' }}>
        <Container maxWidth="md" sx={{ pt: { xs: 14, md: 18 }, pb: { xs: 12, md: 16 } }}>
          {/* Back link */}
          <Box
            component={RouterLink}
            href={paths.index}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 1,
              color: '#52525b',
              textDecoration: 'none',
              fontFamily: '"Roboto Mono", monospace',
              fontSize: 12,
              letterSpacing: 1.5,
              textTransform: 'uppercase',
              mb: { xs: 5, md: 7 },
              transition: 'color 0.2s ease',
              '&:hover': { color: '#0a0a0b' }
            }}
          >
            <SvgIcon sx={{ fontSize: 16 }}><ArrowLeftIcon /></SvgIcon>
            Back to home
          </Box>

          {/* Eyebrow pill */}
          <Stack direction="row" sx={{ mb: { xs: 4, md: 5 } }}>
            <Stack
              direction="row"
              alignItems="center"
              spacing={1.25}
              sx={{
                pl: 0.75,
                pr: 2,
                py: 0.5,
                borderRadius: 999,
                background: '#ffffff',
                border: '1px solid #e4e4e7',
                boxShadow: '0 4px 14px -4px rgba(15, 23, 42, 0.06)'
              }}
            >
              <Box sx={{ width: 22, height: 22, borderRadius: '50%', background: '#2563eb', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#ffffff', fontSize: 12, fontWeight: 700 }}>✶</Box>
              <Typography sx={{ fontFamily: '"Roboto Mono", monospace', fontSize: 11, fontWeight: 500, letterSpacing: 1.5, textTransform: 'uppercase', color: '#0a0a0b' }}>
                Legal · Privacy Policy
              </Typography>
            </Stack>
          </Stack>

          {/* Headline */}
          <Typography
            component="h1"
            sx={{
              fontFamily: '"Plus Jakarta Sans", sans-serif',
              fontSize: { xs: 44, md: 64 },
              fontWeight: 700,
              lineHeight: 1.05,
              letterSpacing: '-0.035em',
              color: '#0a0a0b',
              mb: 3
            }}
          >
            Privacy Policy.
          </Typography>

          <Typography
            sx={{
              fontFamily: '"Roboto Mono", monospace',
              fontSize: 12,
              fontWeight: 500,
              letterSpacing: 1.5,
              textTransform: 'uppercase',
              color: '#71717a',
              mb: { xs: 6, md: 8 }
            }}
          >
            Last Updated May 16, 2026
          </Typography>

          {/* Intro */}
          <Box sx={{ mb: { xs: 8, md: 10 } }}>
            <Typography sx={{ fontFamily: '"Inter", sans-serif', fontSize: { xs: 18, md: 20 }, lineHeight: 1.6, color: '#27272a', mb: 2.5, fontWeight: 500 }}>
              Mystic Media Film ("we," "us," or "our") is committed to protecting your privacy.
            </Typography>
            <Typography sx={{ fontFamily: '"Inter", sans-serif', fontSize: { xs: 17, md: 19 }, lineHeight: 1.6, color: '#52525b' }}>
              This policy explains how we collect, use, share, and safeguard your information when you visit our site or use our services. By using our services, you agree to these practices.
            </Typography>
          </Box>

          {/* Sections */}
          {sections.map((s) => (
            <Box key={s.num} sx={{ pt: { xs: 5, md: 6 }, pb: { xs: 1, md: 2 }, borderTop: '1px solid #e4e4e7' }}>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={{ xs: 3, md: 6 }} alignItems="flex-start">
                <Box sx={{ flexShrink: 0, width: { md: 180 } }}>
                  <Typography sx={{ fontFamily: '"Roboto Mono", monospace', fontSize: 11, fontWeight: 500, letterSpacing: 1.5, textTransform: 'uppercase', color: '#a1a1aa', mb: 1 }}>
                    Section {s.num}
                  </Typography>
                  <Typography sx={{ fontFamily: '"Plus Jakarta Sans", sans-serif', fontSize: { xs: 22, md: 24 }, fontWeight: 600, letterSpacing: '-0.015em', color: '#0a0a0b', lineHeight: 1.2 }}>
                    {s.title}
                  </Typography>
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>{s.body}</Box>
              </Stack>
            </Box>
          ))}

          {/* Contact */}
          <Box sx={{ mt: { xs: 10, md: 14 }, pt: { xs: 6, md: 8 }, borderTop: '1px solid #e4e4e7' }}>
            <Typography sx={{ fontFamily: '"Roboto Mono", monospace', fontSize: 11, fontWeight: 500, letterSpacing: 1.5, textTransform: 'uppercase', color: '#71717a', mb: 1.5 }}>
              Privacy questions?
            </Typography>
            <Typography sx={{ fontFamily: '"Plus Jakarta Sans", sans-serif', fontSize: { xs: 28, md: 36 }, fontWeight: 700, letterSpacing: '-0.025em', color: '#0a0a0b', mb: 3 }}>
              Reach Mystic Media Film.
            </Typography>
            <Stack spacing={1}>
              <Typography sx={{ fontFamily: '"Inter", sans-serif', fontSize: 17, color: '#27272a' }}>
                sales@mysticmediafilm.com
              </Typography>
              <Typography sx={{ fontFamily: '"Inter", sans-serif', fontSize: 17, color: '#27272a' }}>
                (813) 815-0142
              </Typography>
              <Typography sx={{ fontFamily: '"Inter", sans-serif', fontSize: 17, color: '#27272a' }}>
                mysticmediafilm.com
              </Typography>
            </Stack>
          </Box>

          {/* Disclaimer */}
          <Typography sx={{ fontFamily: '"Inter", sans-serif', fontSize: 13, lineHeight: 1.7, color: '#a1a1aa', mt: { xs: 6, md: 8 } }}>
            This policy is provided for informational purposes and is not legal advice. We comply with applicable Texas and federal privacy laws. Consult a qualified attorney for advice specific to your situation.
          </Typography>
        </Container>
      </Box>
    </>
  );
};

export default Page;
